import React from 'react'

interface App {
  app_name: string
  app_token: string
  app_config_file_path?: string
}

interface AppListProps {
  apps: App[]
  selectedApp: string
  isLoadingApps: boolean
  searchTerm: string
  onAppSelection: (app_token: string, app_name: string) => void
  onSearchChange: (searchTerm: string) => void
  onClearSearch: () => void
}

const AppList: React.FC<AppListProps> = ({
  apps,
  selectedApp,
  isLoadingApps,
  searchTerm,
  onAppSelection,
  onSearchChange,
  onClearSearch
}) => {
  // Filter apps based on search term
  const filteredApps = apps
    .filter((app: App) => 
      app.app_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a: App, b: App) => {
      // Always show default app at the top
      const aIsDefault = a.app_name.toLowerCase() === 'default'
      const bIsDefault = b.app_name.toLowerCase() === 'default'
      
      if (aIsDefault && !bIsDefault) return -1
      if (!aIsDefault && bIsDefault) return 1
      
      // If both are default or both are not default, apply canary sorting
      const aHasCanary = a.app_name.toLowerCase().includes('canary')
      const bHasCanary = b.app_name.toLowerCase().includes('canary')
      
      // If both have canary or both don't have canary, maintain original order
      if (aHasCanary === bHasCanary) {
        return 0
      }
      
      // Push canary apps to the bottom
      return aHasCanary ? 1 : -1
    })

  return (
    <div className="w-25 border-end bg-light d-flex flex-column apps-panel">
      <div className="p-4 border-bottom apps-header">
        <h5 className="fw-bold mb-0">Apps</h5>
        <div className="search-container mt-3">
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search apps..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary btn-sm border-start-0"
                type="button"
                onClick={onClearSearch}
                title="Clear search"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="flex-grow-1 overflow-auto">
        <div className="p-4 apps-list" style={{ height: '50vh' }}>
          {isLoadingApps ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0">Loading apps...</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {filteredApps?.length > 0 ? (
                filteredApps.map((app: App) => (
                  <div
                    key={app.app_name}
                    className={`app-item p-3 rounded cursor-pointer ${
                      selectedApp === app.app_name ? 'app-item-selected' : 'app-item-default'
                    }`}
                    onClick={() => onAppSelection(app.app_token, app.app_name)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="fw-bold fs-6">{app.app_name}</div>
                    <div className="fs-8">{app.app_token}</div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-search text-muted fs-1 mb-2"></i>
                  <p className="text-muted mb-0">
                    {searchTerm ? `No apps found for "${searchTerm}"` : 'No apps available'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AppList 